<script lang="ts">
  // The host's push settles the flow; this only turns the clock (long-running-integration §8).
  import { UiKeys } from '@voicedojo/backend-types';
  import { t } from '$lib/i18n';
  import { AnimatedDots, Button, Notice } from '$lib/ui';
  import { msUntilFailed, pendingPhase, pendingStart } from '$lib/utils/time/pendingDeadline';

  interface Props {
    since: string;
    deadlineSeconds: number;
    pendingText?: string;
    failedText: string;
    onRetry: () => Promise<void>;
    /** Once, at the deadline — e.g. one refetch in case the settling push was missed. */
    onDeadline?: () => void;
    'data-testid'?: string;
  }

  let { since, deadlineSeconds, pendingText, failedText, onRetry, onDeadline, 'data-testid': testid }: Props =
    $props();

  let retriedAt = $state<number | null>(null);
  let retrying = $state(false);
  let now = $state(Date.now());
  const start = $derived(pendingStart(since, retriedAt));
  const phase = $derived(pendingPhase(start, deadlineSeconds, now));

  $effect(() => {
    if (phase === 'failed') return;
    const timer = setTimeout(() => {
      now = Date.now();
      onDeadline?.();
    }, msUntilFailed(start, deadlineSeconds, Date.now()));
    return () => clearTimeout(timer);
  });

  async function retry(): Promise<void> {
    retrying = true;
    try {
      await onRetry();
      retriedAt = now = Date.now();
    } finally {
      retrying = false;
    }
  }
</script>

{#if phase === 'failed'}
  <Notice tone="warning" data-testid={testid}>
    {failedText}
    <Button variant="ghost" size="sm" disabled={retrying} onclick={() => void retry()} data-testid="pending-retry">
      {t(UiKeys.Pending.Retry)}{#if retrying}<AnimatedDots />{/if}
    </Button>
  </Notice>
{:else if pendingText}
  <Notice tone="info" data-testid={testid}>{pendingText}<AnimatedDots /></Notice>
{/if}
